import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Download,
  Loader2,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  clarifyOffer,
  getOffer,
  getOfferComparison,
  selectOffer,
  API,
} from "../lib/api";
import QuotationCard from "../components/rfq/QuotationCard";
import { WORKSPACE } from "../constants/testIds";

const SORTS = [
  { key: "lowest_price", label: "Lowest Price" },
  { key: "shortest_lead_time", label: "Shortest Lead Time" },
  { key: "fastest_delivery", label: "Fastest Delivery" },
  { key: "payment_terms", label: "Payment Terms" },
  { key: "supplier_rating", label: "Supplier Rating" },
  { key: "newest", label: "Newest Offer" },
];

export default function BuyerOffers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("lowest_price");
  const [incotermFilter, setIncotermFilter] = useState("");
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clarifyMsg, setClarifyMsg] = useState("");

  const load = async (sortKey = sort) => {
    setLoading(true);
    try {
      const res = await getOfferComparison(id, {
        sort: sortKey,
        incoterm: incotermFilter || undefined,
      });
      setData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const offers = data?.offers || [];
  const groups = data?.groups || [];
  const multi = groups.length > 0;
  const hasSelection = useMemo(
    () => offers.some((o) => o.status === "Selected"),
    [offers]
  );

  const selectionBlockedFor = (groupOffers) => {
    if (!multi) return hasSelection;
    return (groupOffers || []).some((o) => o.status === "Selected");
  };

  const openDetail = async (offerId) => {
    try {
      const detail = await getOffer(offerId);
      setSelectedDetail(detail);
      setClarifyMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open offer");
    }
  };

  const onSelect = async (offerId, groupOffers) => {
    if (selectionBlockedFor(groupOffers)) {
      toast.error(
        multi
          ? "A supplier is already selected for this product"
          : "A supplier is already selected for this RFQ"
      );
      return;
    }
    setBusy(true);
    try {
      const res = await selectOffer(offerId);
      const poId = res.purchase_order?.id;
      toast.success(
        res.purchase_order?.number
          ? `PO ${res.purchase_order.number} created`
          : res.next_phase || "Supplier selected"
      );
      setSelectedDetail(null);
      if (poId && !multi) {
        navigate(`/workspace/purchase-orders/${poId}`);
        return;
      }
      await load();
      if (poId) {
        toast.message("Purchase order created — continue selecting for other products if needed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Selection failed");
    } finally {
      setBusy(false);
    }
  };

  const onClarify = async () => {
    if (!selectedDetail || !clarifyMsg.trim()) return;
    try {
      await clarifyOffer(selectedDetail.id, clarifyMsg.trim());
      toast.success("Clarification request sent to operations");
      setClarifyMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.offersPage}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={`/rfq/${id}`} className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              Offer Comparison
            </span>
          </Link>
          <Link to={`/rfq/${id}`} className="text-sm text-neutral-500 hover:text-neutral-900">
            RFQ detail
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <Link
          to={`/rfq/${id}`}
          className="inline-flex items-center rounded-sm -ml-2 text-neutral-600 hover:text-neutral-900 text-sm h-10 px-3"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to RFQ
        </Link>

        {loading ? (
          <div className="p-20 grid place-items-center">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
          </div>
        ) : (
          <>
            <section className="border border-neutral-200 bg-white rounded-sm p-6">
              <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
                Your RFQ
              </div>
              <h1 className="mt-3 text-3xl font-light tracking-tight font-mono text-[hsl(217,90%,25%)]">
                {data?.rfq?.number}
              </h1>
              <p className="mt-3 text-neutral-600">
                {data?.rfq?.product} · {data?.rfq?.quantity} · {data?.rfq?.destination_country}
                {data?.rfq?.product_count > 1
                  ? ` · ${data.rfq.product_count} products`
                  : ""}
              </p>
              {data?.po_ready && (
                <div className="mt-4 inline-flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 px-3 py-2 rounded-sm">
                  <Check className="w-4 h-4" /> Purchase Order Ready
                </div>
              )}
            </section>

            <section className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                  Published quotations
                </h2>
                <p className="mt-2 text-sm text-neutral-500">
                  Compare offers and select the best supplier. Only published quotations are shown.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <select
                  data-testid={WORKSPACE.sortSelect}
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    load(e.target.value);
                  }}
                  className="h-10 border border-neutral-300 rounded-sm px-3 text-sm bg-white"
                >
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <Input
                  data-testid={WORKSPACE.filterIncoterm}
                  value={incotermFilter}
                  onChange={(e) => setIncotermFilter(e.target.value)}
                  onBlur={() => load()}
                  placeholder="Filter Incoterm"
                  className="h-10 w-36 rounded-sm"
                />
              </div>
            </section>

            {multi ? (
              <div className="space-y-8" data-testid={WORKSPACE.comparisonTable}>
                {groups.map((g) => (
                  <section key={g.product_id || g.label} className="space-y-3">
                    <div className="border border-neutral-200 bg-white rounded-sm p-4">
                      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                        Product
                      </div>
                      <h3 className="mt-1 text-xl font-medium">{g.label}</h3>
                      <p className="text-sm text-neutral-500 mt-1">{g.quantity_label}</p>
                    </div>
                    <OfferList
                      offers={g.offers || []}
                      busy={busy}
                      blocked={selectionBlockedFor(g.offers)}
                      onView={openDetail}
                      onSelect={(oid) => onSelect(oid, g.offers)}
                    />
                  </section>
                ))}
              </div>
            ) : (
              <OfferList
                offers={offers}
                busy={busy}
                blocked={hasSelection}
                onView={openDetail}
                onSelect={(oid) => onSelect(oid, offers)}
              />
            )}

            {selectedDetail && (
              <section
                data-testid={WORKSPACE.offerDetail}
                className="border border-neutral-200 bg-white rounded-sm p-6 lg:p-8 space-y-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500">
                      Offer details
                    </div>
                    <h3 className="mt-2 text-2xl font-light tracking-tight">
                      {selectedDetail.supplier?.name}
                    </h3>
                  </div>
                  <Button variant="ghost" className="rounded-sm" onClick={() => setSelectedDetail(null)}>
                    Close
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <Meta label="Unit Price" value={`${selectedDetail.unit_price} ${selectedDetail.currency}`} />
                  <Meta label="Total" value={selectedDetail.total_price ?? "—"} />
                  <Meta label="Incoterm" value={selectedDetail.incoterm} />
                  <Meta label="MOQ" value={selectedDetail.moq} />
                  <Meta label="Lead Time" value={selectedDetail.lead_time} />
                  <Meta label="Packaging" value={selectedDetail.packaging} />
                  <Meta label="Payment Terms" value={selectedDetail.payment_terms} />
                  <Meta label="Validity" value={selectedDetail.offer_validity} />
                  <Meta label="Origin" value={selectedDetail.origin} />
                  <Meta label="Certificates" value={(selectedDetail.certificates || []).join(", ")} />
                  <Meta label="Capacity" value={selectedDetail.production_capacity} />
                  <Meta label="Remarks" value={selectedDetail.remarks} />
                </div>

                {(selectedDetail.attachments || []).length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-2">
                      Attachments
                    </div>
                    <ul className="space-y-2">
                      {selectedDetail.attachments.map((a) => (
                        <li key={a.file_id}>
                          <a
                            href={`${API}/rfq/attachment/${a.file_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-sm text-[hsl(217,90%,25%)] hover:underline"
                          >
                            <Download className="w-4 h-4 mr-2" /> {a.filename}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-3">
                    Offer timeline
                  </div>
                  <ol className="space-y-2">
                    {(selectedDetail.timeline || []).map((t) => (
                      <li key={t.key} className="flex items-center gap-3 text-sm">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            t.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                          }`}
                        />
                        <span className="font-medium">{t.label}</span>
                        <span className="text-neutral-400">
                          {t.at ? new Date(t.at).toLocaleString() : "—"}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>

                {selectedDetail.status === "Published" &&
                  !selectionBlockedFor(
                    multi
                      ? offers.filter((o) => o.product_id === selectedDetail.product_id)
                      : offers
                  ) && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button
                      className="rounded-sm h-11 bg-[hsl(217,90%,32%)] text-white"
                      disabled={busy}
                      onClick={() =>
                        onSelect(
                          selectedDetail.id,
                          multi
                            ? offers.filter((o) => o.product_id === selectedDetail.product_id)
                            : offers
                        )
                      }
                    >
                      <Check className="w-4 h-4 mr-2" /> Select this supplier
                    </Button>
                  </div>
                )}

                <div className="border-t border-neutral-100 pt-6">
                  <div className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 mb-3">
                    Request clarification
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={clarifyMsg}
                      onChange={(e) => setClarifyMsg(e.target.value)}
                      placeholder="Ask operations about this offer…"
                      className="h-11 rounded-sm flex-1"
                      data-testid={WORKSPACE.clarifyInput}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-sm h-11"
                      onClick={onClarify}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" /> Send
                    </Button>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">{label}</div>
      <div className="mt-1 text-neutral-800 break-words">{value || "—"}</div>
    </div>
  );
}

function OfferList({ offers, busy, blocked, onView, onSelect, onClarify }) {
  if (!offers.length) {
    return (
      <div className="border border-neutral-200 bg-white rounded-sm p-10 text-center text-sm text-neutral-500">
        No published quotations yet for this product.
      </div>
    );
  }
  return (
    <section className="space-y-4" data-testid={WORKSPACE.comparisonTable}>
      {offers.map((o) => (
        <QuotationCard
          key={o.id}
          offer={o}
          busy={busy}
          blocked={blocked}
          onView={onView}
          onSelect={onSelect}
          onClarify={onClarify ? () => onView(o.id) : undefined}
        />
      ))}
    </section>
  );
}
